<script lang="ts">
	import { writable, type Writable } from 'svelte/store'
	import { onMount, onDestroy, setContext, tick } from 'svelte'
	import { t } from '$i18n/i18n'
	import { fly } from 'svelte/transition'
	import { closeModalOnOutsideClick, manualOpenWhitelist } from '../stores'
	import { breadcrumbs } from '$lib/organisms/Breadcrumbs/breadcrumbs.svelte'
	import { LotType } from '$types/enums'
	import Notifications from '../organisms/Notifications.svelte'
	import Button from '$lib/atoms/Button.svelte'
	import Breadcrumbs from '../organisms/Breadcrumbs.svelte'
	import {
		WhitelistFinishedHandler,
		WhitelistMaterialTypePageHandler,
		whitelistModalUtils,
		WhitelistOverviewHandler,
		WhitelistStartPageHandler
	} from './whitelistModalUtil'
	import { WhitelistView } from './whitelist'
	import { DefaultRobotoFontProps } from '$types/fontProps'
	import {
		AllWhitelistData,
		type WhitelistCatalog,
		type WhitelistData,
		type WhitelistDataTransportMaterial,
		type WhitelistSelectorData
	} from './whitelistData'
	import WhitelistOverviewPage from './WhitelistOverviewPage.svelte'
	import WhitelistSelectMaterialsPage from './WhitelistSelectMaterialsPage.svelte'
	import WhitelistConfigFinishedPage from './WhitelistConfigFinishedPage.svelte'
	import { fetchUtils } from '$utils/fetchUtils'
	import WhitelistSelectTypesPage from './WhitelistSelectTypesPage.svelte'
	import ProgressBar from '../molecules/ProgressBar.svelte'
	import { modals } from 'svelte-modals'
	import { getJwt } from '$utils/jwt'
	import { TooltipPlacements } from '$types/enums'
	import Tooltip from '../atoms/Tooltip.svelte'
	import Icon from '../atoms/Icon.svelte'
	import closeIcon from '@/icons/icon_close.svg'

	// constants
	const progressBarStates: WhitelistView[] = [
		WhitelistView.START_PAGE,
		WhitelistView.MATERIAL_TYPE,
		WhitelistView.OVERVIEW
	]

	// exported variables
	export let isOpen: boolean = true

	/**
	 * per-instance breadcrumb context. The `breadcrumbs` store is a module-level
	 * singleton shared with the maplibre FSM (a permanent module singleton) and
	 * the Breadcrumbs stories, all of which use the 'default' context. In a shared
	 * browser (Storybook full-suite CI) that makes the trail leak across instances
	 * and mis-navigate. A unique context per modal isolates this trail;
	 * Breadcrumbs.svelte already accepts a `context` prop
	 */
	const breadcrumbsContext = setContext('breadcrumbsContext', crypto.randomUUID())

	/**
	 * teardown: delete this modal's per-instance breadcrumb context from the module-level
	 * singleton `breadcrumbs` store. `clear()` only empties the list, leaving the UUID key
	 * behind — across many story mounts in a shared browser (Storybook full-suite CI) those
	 * stale keys accumulate unboundedly. `remove()` deletes the entry entirely
	 */
	let destroyed = false
	onDestroy(() => {
		destroyed = true
		breadcrumbs.remove(breadcrumbsContext)
	})

	// context stores
	let whitelistConfigData: Writable<Map<LotType, WhitelistCatalog[]>> = setContext(
		'whitelistConfigData',
		writable(new Map<LotType, WhitelistCatalog[]>())
	)
	let whitelistTransportConfigData: Writable<Map<LotType, WhitelistSelectorData[]>> = setContext(
		'whitelistTransportConfigData',
		writable(new Map<LotType, WhitelistSelectorData[]>())
	)
	let selectedWhitelistTransportData: Writable<Map<LotType, Map<string, string[]>>> = setContext(
		'selectedWhitelistTransportData',
		writable(new Map<LotType, Map<string, string[]>>())
	)
	let selectedUserData: Writable<AllWhitelistData> = setContext(
		'selectedWhitelistData',
		writable(new AllWhitelistData())
	)
	let selectedLotTypes: Writable<LotType[]> = setContext('selectedLotTypes', writable([]))
	let currentLotType: Writable<LotType> = setContext('currentLotType', writable())

	// view handling
	let view: WhitelistView = WhitelistView.START_PAGE
	let viewHandler:
		| WhitelistStartPageHandler
		| WhitelistMaterialTypePageHandler
		| WhitelistOverviewHandler
		| WhitelistFinishedHandler = new WhitelistStartPageHandler()
	let padding: string = '56px 56px 50px'
	let modalHeight: string = 'max-content'
	let progressBarMargin: string = '24px'

	//globals
	let lotTypes: LotType[] = []
	let errorMaterials: string[] = [] // once the check is made on WhitelistSelectMaterialsPage, it contains the materials that have no properties/list elements
	let firstActionButtonDisabled: boolean = $selectedLotTypes.length === 0
	let fourthButtonText: string = $t('UI.whitelist.modal.fourthActionButton.label.selectAll')
	let quickFinishButtonDisabled: boolean = true
	let showQuickFinishButton: boolean = false

	let selectMaterialsPage: WhitelistSelectMaterialsPage | null = null
	let overviewWeiterTooltip: {
		enable: () => void
		disable: () => void
		hide: () => void
		show: (force: boolean) => void
	} | null = null

	onMount(async () => {
		await loadData()
		if (destroyed) return

		$closeModalOnOutsideClick = false
		viewHandler = new WhitelistStartPageHandler()
		breadcrumbs.clear(breadcrumbsContext)
		updateBreadcrumbs(viewHandler.breadcrumbTitle, WhitelistView.START_PAGE)
	})

	// fetch functions

	async function loadData() {
		await getConfigData()
		await getUserData()
	}

	// point of entry for view
	// HINT: to avoid if else-if or speculative* if-else we already opt for switch-case even with two options
	// this'll make it easier to add new types
	// *speculative: just because if conditions isn't true doesn't imply the else condition is true
	function applyWhitelistDataItem(item: WhitelistData): void {
		switch (item.type) {
			case 'optimizeMaterials': {
				$whitelistConfigData.set(item.lotType, item.catalogs)
				const hasMaterials = item.catalogs?.some((c) => c.materials && c.materials.length > 0)
				if (!hasMaterials) return
				lotTypes = [...lotTypes, item.lotType]
				return
			}
			case 'transportMaterials': {
				$whitelistTransportConfigData.set(item.lotType, item.materials)
				if (!item.materials?.length) return
				lotTypes = [...lotTypes, item.lotType]
				return
			}
		}
	}

	async function getConfigData() {
		await fetchUtils.get(`/api/whitelist/fetchConfig`, false).then((data) => {
			for (const whitelistData of data.whitelistData as WhitelistData[]) {
				applyWhitelistDataItem(whitelistData)
			}
			updateFourthButtonText()
		})
	}

	// HINT: keep shape in sync with getConfigData
	async function getUserData() {
		await fetchUtils.get('/api/whitelist/fetch').then((data) => {
			if (data?.whitelistData) {
				$selectedUserData.init(data.whitelistData)
				for (const item of data.whitelistData as WhitelistData[]) {
					if (item.type !== 'transportMaterials') continue
					const transportData = item as WhitelistDataTransportMaterial
					const selectorMap = new Map<string, string[]>()
					for (const selector of transportData.materials ?? []) {
						if (selector.entries?.length) {
							selectorMap.set(selector.nodeSelector, [...selector.entries])
						}
					}
					if (selectorMap.size > 0) {
						$selectedWhitelistTransportData.set(transportData.lotType, selectorMap)
					}
				}
				$selectedWhitelistTransportData = new Map($selectedWhitelistTransportData)
				data.whitelistData.forEach(
					(data: WhitelistData) => ($selectedLotTypes = [...$selectedLotTypes, data.lotType])
				)
			}
		})
	}

	function reorderSelectedLotTypesForMaterialFlow() {
		if (!$selectedLotTypes?.length) return
		const transportFirst = $selectedLotTypes.includes(LotType.TRANSPORT) ? [LotType.TRANSPORT] : []
		const rest = lotTypes.filter(
			(lt) => lt !== LotType.TRANSPORT && $selectedLotTypes.includes(lt as LotType)
		) as LotType[]
		$selectedLotTypes = [...transportFirst, ...rest]
	}

	function updateQuickFinishButtonState() {
		showQuickFinishButton = view === WhitelistView.MATERIAL_TYPE
		if (!showQuickFinishButton) {
			quickFinishButtonDisabled = true
			return
		}
		// Keep disabled state aligned with "Weiter" button
		quickFinishButtonDisabled = firstActionButtonDisabled
	}

	function getTransportSelectionCount(lotType: LotType): number {
		const selectors = $selectedWhitelistTransportData.get(lotType)
		if (!selectors) return 0
		let count = 0
		for (const entries of selectors.values()) {
			count += entries.length
		}
		return count
	}

	function getSelectedTransportDataAsObject(): WhitelistData[] {
		const result: WhitelistData[] = []
		for (const [lotType, selectors] of $selectedWhitelistTransportData.entries()) {
			if (!selectors || selectors.size === 0) continue
			const configuredSelectors = $whitelistTransportConfigData.get(lotType) ?? []
			const materials: WhitelistSelectorData[] = []
			for (const configuredSelector of configuredSelectors) {
				const selectedEntries = selectors.get(configuredSelector.nodeSelector) ?? []
				if (!selectedEntries.length) continue
				materials.push({
					label: configuredSelector.label,
					nodeSelector: configuredSelector.nodeSelector,
					entries: [...selectedEntries]
				})
			}
			if (!materials.length) continue
			result.push({
				lotType,
				type: 'transportMaterials',
				materials
			})
		}
		return result
	}

	async function saveWhitelist() {
		let whitelistData: WhitelistData[] = [
			...$selectedUserData.getAllDataAsObject(),
			...getSelectedTransportDataAsObject()
		]
		let dataJSON = { whitelistData: whitelistData } // Convert to JsonObject because Sirius can't handle JsonArrays
		await fetchUtils.post('/api/whitelist/save', dataJSON, false)
	}

	// Breadcrumbs handling

	function updateBreadcrumbs(
		title: string,
		newView: WhitelistView,
		lotType: LotType | undefined = undefined
	) {
		breadcrumbs.push(
			{
				title,
				data: {
					view: newView,
					lotType
				},
				callback: () => handleBreadcrumbs(newView, lotType)
			},
			breadcrumbsContext
		)
	}

	function handleBreadcrumbs(newView: WhitelistView, lotType: LotType | undefined = undefined) {
		view = newView
		if (view !== WhitelistView.OVERVIEW) {
			breadcrumbs.popUntil(0, breadcrumbsContext)
		}
		if (view === WhitelistView.MATERIAL_TYPE) {
			const breadcrumbTitle = whitelistModalUtils.getBreadcrumbTitleMaterialType($currentLotType)
			updateBreadcrumbs(breadcrumbTitle, WhitelistView.MATERIAL_TYPE, lotType)
		}
		if (lotType) {
			$currentLotType = lotType as LotType
		}
	}

	function handleFirstActionButtonStartPage() {
		reorderSelectedLotTypesForMaterialFlow()
		view = WhitelistView.MATERIAL_TYPE
		$currentLotType = $selectedLotTypes[0]
		const breadcrumbTitle = whitelistModalUtils.getBreadcrumbTitleMaterialType($currentLotType)
		updateBreadcrumbs(breadcrumbTitle, WhitelistView.MATERIAL_TYPE, $currentLotType)
		updateFourthButtonText()

		// we need to reset the selection for deselected lot types
		;[...lotTypes]
			.filter((lotType) => !$selectedLotTypes.includes(lotType as LotType)) // find LotTypes that are no more selected
			.forEach((deselectedLotType) => {
				const lotType = deselectedLotType as LotType
				$selectedUserData.resetSelection(lotType)
				// Keep transport state in sync with lot-type deselection from step 1.
				$selectedWhitelistTransportData.delete(lotType)
			}) // reset selection for those
		$selectedWhitelistTransportData = new Map($selectedWhitelistTransportData)

		updateFirstActionButtonDisabled()
	}

	function handleNextPageOrNextLotType() {
		const currentLotTypeIndex = $selectedLotTypes.findIndex((type) => type === $currentLotType)
		if (currentLotTypeIndex < 0) return
		const allLotTypesSelected = currentLotTypeIndex === $selectedLotTypes.length - 1
		if (allLotTypesSelected) {
			// go to next page
			breadcrumbs.pop(breadcrumbsContext)

			updateBreadcrumbs(
				$t('UI.whitelist.modal.breadcrumb.materialTypeTitle.finished'),
				WhitelistView.MATERIAL_TYPE,
				$selectedLotTypes.at(-1)
			)
			view = WhitelistView.OVERVIEW
		} else {
			// go to next LotType
			const nextLotTypeIndex = currentLotTypeIndex + 1
			breadcrumbs.setContext(
				breadcrumbs.get(breadcrumbsContext).filter((b) => !b.data?.lotType),
				breadcrumbsContext
			)
			$currentLotType = $selectedLotTypes[nextLotTypeIndex]
			const breadcrumbTitle = whitelistModalUtils.getBreadcrumbTitleMaterialType($currentLotType)
			updateBreadcrumbs(
				breadcrumbTitle,
				WhitelistView.MATERIAL_TYPE,
				$selectedLotTypes[nextLotTypeIndex]
			)
			updateFourthButtonText()
		}
	}

	// action button handling
	async function validateCurrentLotTypeSelection(): Promise<boolean> {
		errorMaterials = $selectedUserData.checkSelectedMaterials($currentLotType)
		if (errorMaterials.length > 0) {
			await tick() // wait until bindings/render complete before focusing first invalid material
			if (selectMaterialsPage) {
				selectMaterialsPage.updateActiveElementsWithError()
			}
			return false
		}
		return true
	}

	function hasMaterialSelectionsForCurrentLotType(): boolean {
		const hasMaterials = $selectedUserData.getMaterialCountForType($currentLotType) > 0
		const hasTransport = getTransportSelectionCount($currentLotType) > 0
		return hasMaterials || hasTransport
	}

	async function handleFirstActionButton() {
		switch (view) {
			case WhitelistView.START_PAGE:
				handleFirstActionButtonStartPage()
				break
			case WhitelistView.MATERIAL_TYPE:
				// If materials are checked, validate their config; if none checked, allow navigation
				if (hasMaterialSelectionsForCurrentLotType()) {
					if (!(await validateCurrentLotTypeSelection())) {
						return
					}
				}
				handleNextPageOrNextLotType()
				break
			case WhitelistView.OVERVIEW:
				// Remove unconfigured transport lotType so it behaves as if never selected
				if (
					$selectedLotTypes.includes(LotType.TRANSPORT) &&
					getTransportSelectionCount(LotType.TRANSPORT) === 0
				) {
					$selectedLotTypes = $selectedLotTypes.filter((lt) => lt !== LotType.TRANSPORT)
				}
				view = WhitelistView.FINISHED
				await saveWhitelist()
				break
			case WhitelistView.FINISHED:
				await getJwt()
				modals.close()
				isOpen = false
				$manualOpenWhitelist = false
				$closeModalOnOutsideClick = true // reset the value to default
		}
	}

	function handleCloseModal() {
		overviewWeiterTooltip?.disable()
		modals.close()
		isOpen = false
		$manualOpenWhitelist = false
		$closeModalOnOutsideClick = true
	}

	async function handleQuickFinishToOverview() {
		if (firstActionButtonDisabled || quickFinishButtonDisabled) return
		if (view === WhitelistView.MATERIAL_TYPE && hasMaterialSelectionsForCurrentLotType()) {
			if (!(await validateCurrentLotTypeSelection())) {
				return
			}
		}
		breadcrumbs.pop(breadcrumbsContext)
		updateBreadcrumbs(
			$t('UI.whitelist.modal.breadcrumb.materialTypeTitle.finished'),
			WhitelistView.MATERIAL_TYPE,
			$selectedLotTypes.at(-1)
		)
		view = WhitelistView.OVERVIEW
	}

	// back button functionality

	function handleBackButtonMaterialTypePage() {
		// if breadcrumbs only contain 1 lot type
		// switch view and update breadcrumb
		const lastBreadcrumb = breadcrumbs.get(breadcrumbsContext).at(-1)
		if (!lastBreadcrumb) return

		const actualBreadcrumbLotType = lastBreadcrumb.data.lotType // get last breadcrumb entry
		const backToStartpage = actualBreadcrumbLotType === $selectedLotTypes[0]
		if (backToStartpage) {
			view = WhitelistView.START_PAGE
			breadcrumbs.pop(breadcrumbsContext)
		} else {
			breadcrumbs.pop(breadcrumbsContext)
			const previousLotTypeIndex =
				$selectedLotTypes.findIndex((chip) => chip === actualBreadcrumbLotType) - 1
			if (previousLotTypeIndex > -1) {
				$currentLotType = $selectedLotTypes[previousLotTypeIndex]
				const breadcrumbTitle = whitelistModalUtils.getBreadcrumbTitleMaterialType($currentLotType)
				updateBreadcrumbs(
					breadcrumbTitle,
					WhitelistView.MATERIAL_TYPE,
					$selectedLotTypes[previousLotTypeIndex]
				)
			}
		}
	}

	function handleBackButtonOverviewPage() {
		breadcrumbs.pop(breadcrumbsContext)
		breadcrumbs.pop(breadcrumbsContext) // remove also last material type element
		const breadcrumbTitle = whitelistModalUtils.getBreadcrumbTitleMaterialType(
			$currentLotType ?? $selectedLotTypes.at(-1)
		)
		updateBreadcrumbs(breadcrumbTitle, WhitelistView.MATERIAL_TYPE, $selectedLotTypes.at(-1))
		view = WhitelistView.MATERIAL_TYPE
		updateFourthButtonText()
	}

	function handleBackButton() {
		// switch view
		switch (view) {
			// second view
			case WhitelistView.MATERIAL_TYPE:
				handleBackButtonMaterialTypePage()
				break
			// third view
			case WhitelistView.OVERVIEW:
				handleBackButtonOverviewPage()
		}
	}

	// second action button, used on the first page to select ALL lotTypes
	function selectAllLotTypesAndMaterials() {
		// eslint-disable-next-line svelte/prefer-svelte-reactivity
		let uniqueSelectedLotTypes = new Set($selectedLotTypes || [])

		lotTypes.forEach((lotType) => {
			let catalogsForLotType = $whitelistConfigData.get(lotType as LotType) || [] // get available data from config
			$selectedUserData.resetSelection(lotType)
			$selectedUserData.addAllCatalogs(lotType, catalogsForLotType)
			uniqueSelectedLotTypes.add(lotType as LotType)
		})

		// For "select all", transport must be explicitly defined as "Ja" (backend value: "yes").
		const transportSelectors = $whitelistTransportConfigData.get(LotType.TRANSPORT) || []
		if (transportSelectors.length > 0) {
			const transportSelection = new Map<string, string[]>()
			transportSelectors.forEach((selector) => {
				const normalizedEntries = (selector.entries || []).map((entry) => entry.toLowerCase())
				let yesValue = selector.entries.find((entry) => entry.toLowerCase() === 'yes')
				if (!yesValue && normalizedEntries.includes('ja')) {
					yesValue = selector.entries.find((entry) => entry.toLowerCase() === 'ja')
				}
				if (yesValue) {
					transportSelection.set(selector.nodeSelector, [yesValue])
				}
			})
			if (transportSelection.size > 0) {
				$selectedWhitelistTransportData.set(LotType.TRANSPORT, transportSelection)
				$selectedWhitelistTransportData = new Map($selectedWhitelistTransportData)
				uniqueSelectedLotTypes.add(LotType.TRANSPORT)
			}
		}

		$selectedLotTypes = [...uniqueSelectedLotTypes]
		reorderSelectedLotTypesForMaterialFlow()
		selectedUserData = selectedUserData // TODO: IS - confirm we don't need destructring here

		let lastSelectedLotType = $selectedLotTypes.at(-1)
		if (!lastSelectedLotType) return

		$currentLotType = lastSelectedLotType // update, as this would be set when going step by step and prevents a crash
		updateBreadcrumbs(
			$t('UI.whitelist.modal.breadcrumb.materialTypeTitle.finished'),
			WhitelistView.MATERIAL_TYPE,
			lastSelectedLotType
		)
		view = WhitelistView.OVERVIEW
		updateFirstActionButtonDisabled()
	}

	function areAllMaterialsAndPropertiesSelected() {
		let allSelected = true
		let configCatalogs = $whitelistConfigData.get($currentLotType as LotType) || []
		let catalogs = $selectedUserData.getDataForLotType($currentLotType as LotType)
		for (let catalog of configCatalogs) {
			let selectedCatalog = catalogs.find((c) => c.id === catalog.id)
			if (!selectedCatalog || !selectedCatalog.materials) {
				return false
			}
			let [allPropertiesSelected, allMaterialsSelected] = $selectedUserData.getMaterialEqualData(
				catalog.materials,
				selectedCatalog.materials
			)
			allSelected = allSelected && allMaterialsSelected && allPropertiesSelected
		}
		return allSelected
	}

	function selectAllMaterials() {
		let shouldSelectAll = !areAllMaterialsAndPropertiesSelected()
		let catalogsForLotType = $whitelistConfigData.get($currentLotType) || []
		if (shouldSelectAll) {
			$selectedUserData.addAllCatalogs($currentLotType, catalogsForLotType)
			errorMaterials = []
		} else {
			for (let catalog of catalogsForLotType) {
				for (let material of catalog.materials) {
					$selectedUserData.removeMaterialKey($currentLotType, material, catalog)
				}
			}
		}
		fourthButtonText = shouldSelectAll
			? $t('UI.whitelist.modal.fourthActionButton.label.deselectAll')
			: $t('UI.whitelist.modal.fourthActionButton.label.selectAll')
		$selectedUserData = $selectedUserData // TODO: IS - confirm we don't need destructring here
		updateFirstActionButtonDisabled()
	}

	function handleMaterialSelectionChange() {
		updateFourthButtonText()
		updateFirstActionButtonDisabled()
	}

	function updateFourthButtonText() {
		if ($currentLotType && view === WhitelistView.MATERIAL_TYPE) {
			if (areAllMaterialsAndPropertiesSelected()) {
				fourthButtonText = $t('UI.whitelist.modal.fourthActionButton.label.deselectAll')
			} else {
				fourthButtonText = $t('UI.whitelist.modal.fourthActionButton.label.selectAll')
			}
		}
	}

	function updateViewVariables() {
		switch (view) {
			case WhitelistView.START_PAGE:
				progressBarMargin = '24px'
				viewHandler = new WhitelistStartPageHandler()
				break
			case WhitelistView.MATERIAL_TYPE:
				progressBarMargin = '32px'
				viewHandler = new WhitelistMaterialTypePageHandler()
				break
			case WhitelistView.OVERVIEW:
				progressBarMargin = '32px'
				viewHandler = new WhitelistOverviewHandler()
				updateBreadcrumbs(viewHandler.breadcrumbTitle, WhitelistView.OVERVIEW)
				break
			case WhitelistView.FINISHED:
				viewHandler = new WhitelistFinishedHandler()
				break
		}
	}

	function updateFirstActionButtonDisabled() {
		if (view === WhitelistView.START_PAGE) {
			firstActionButtonDisabled = $selectedLotTypes.length === 0
		} else if (view === WhitelistView.MATERIAL_TYPE) {
			// No blocking for any lot type including transport
			firstActionButtonDisabled = false
		} else if (view === WhitelistView.OVERVIEW) {
			// "Weiter" in overview is always enabled
			firstActionButtonDisabled = false
		}
	}

	function getUnspecifiedLotTypes(): LotType[] {
		const unspecified: LotType[] = []
		for (const lotType of $selectedLotTypes) {
			if (lotType === LotType.TRANSPORT) {
				if (getTransportSelectionCount(LotType.TRANSPORT) === 0) unspecified.push(lotType)
				continue
			}
			const materialCount = $selectedUserData.getMaterialCountForType(lotType)
			if (materialCount === 0) unspecified.push(lotType)
		}
		return unspecified
	}

	function buildOverviewWarningMessage(unspecifiedLotTypes: LotType[]): string {
		const lotTypes = unspecifiedLotTypes
			.map((lt) => `${whitelistModalUtils.getLotTypeStringResource(lt)}`)
			.join(', ')
		return $t('UI.whitelist.modal.overview.finish.warning', {
			lotTypes: lotTypes
		})
	}

	let overviewWarningMsg: string = ''

	async function showOverviewWarningTooltip() {
		if (view !== WhitelistView.OVERVIEW) return
		const unspecified = getUnspecifiedLotTypes()
		if (unspecified.length === 0) return
		overviewWarningMsg = buildOverviewWarningMessage(unspecified)
		await tick()
		if (overviewWeiterTooltip) {
			overviewWeiterTooltip.enable()
			overviewWeiterTooltip.show(true)
		}
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-expressions
	$: if (view === WhitelistView.OVERVIEW) showOverviewWarningTooltip()
	$: if (view !== WhitelistView.OVERVIEW && overviewWeiterTooltip) {
		overviewWeiterTooltip.hide()
		overviewWeiterTooltip.disable()
	}
	// eslint-disable-next-line @typescript-eslint/no-unused-expressions
	$: (view, updateViewVariables())
	// eslint-disable-next-line @typescript-eslint/no-unused-expressions
	$: ($currentLotType, updateFourthButtonText())
	// eslint-disable-next-line @typescript-eslint/no-unused-expressions
	$: ($selectedLotTypes,
		$selectedUserData,
		$selectedWhitelistTransportData,
		$currentLotType,
		view,
		updateFirstActionButtonDisabled())
	$: ($selectedLotTypes,
		$selectedUserData,
		$selectedWhitelistTransportData,
		$currentLotType,
		view,
		updateQuickFinishButtonState())
	$: showTransportSpecificationError = false
	$: if (isOpen) {
		document.body.style.overflowY = 'hidden'
	} else {
		document.body.style.overflowY = 'initial'
	}
</script>

{#if isOpen}
	<div
		role="dialog"
		class="modal modal-backdrop"
		in:fly={{ y: -1000, duration: 400 }}
		out:fly={{ y: -1000, duration: 400 }}
	>
		<div
			class="contents"
			style="--content-padding: {padding}; height: {modalHeight}; max-height:48rem; max-width: 57rem; width: 57rem;justify-content: flex-start"
		>
			<Notifications target="modal" />
			<div class="whitelist-modal-header">
				{#if view !== WhitelistView.FINISHED}
					<Breadcrumbs
						context={breadcrumbsContext}
						fontProps={DefaultRobotoFontProps('1.3125rem', '0.875rem', '#9d9d9d', '#8D8D8D', 500)}
					/>
				{/if}
				<Icon
					clickable
					on:click={handleCloseModal}
					size={24}
					src={closeIcon}
					titleAttr={$t('UI.button.close')}
				/>
			</div>
			<div class="whitelist-modal-title">
				<h2>{@html viewHandler.title}</h2>
			</div>
			<div class="actual-content">
				<p class="modal-message">{@html viewHandler.description}</p>
				{#if view !== WhitelistView.FINISHED}
					<div
						class="wrapper-whitelist-progress-bar"
						style="--progress-bar-margin-bottom:{progressBarMargin}"
					>
						<ProgressBar
							states={progressBarStates}
							actualState={view}
							barHeight={1.25}
							iconDistance={23}
						/>
					</div>
				{/if}
				{#if view === WhitelistView.START_PAGE}
					<WhitelistSelectTypesPage {lotTypes} />
				{:else if view === WhitelistView.MATERIAL_TYPE}
					<WhitelistSelectMaterialsPage
						bind:errorMaterials
						bind:this={selectMaterialsPage}
						updateBreadcrumbs={(title, view, lotType) => updateBreadcrumbs(title, view, lotType)}
						onMaterialSelectionChange={handleMaterialSelectionChange}
					/>
				{:else if view === WhitelistView.OVERVIEW}
					<WhitelistOverviewPage {lotTypes} />
				{:else if view === WhitelistView.FINISHED}
					<WhitelistConfigFinishedPage />
				{/if}
			</div>
			<div class="whitelist-button-actions">
				<div>
					<!-- the back button -->
					{#if whitelistModalUtils.showThirdActionButton(view)}
						<div class="third-action-button">
							<Button variant="secondary" color="green" onclick={handleBackButton}
								>{$t('UI.whitelist.modal.thirdActionButton.label')}</Button
							>
						</div>
					{/if}
					<!-- this button is only visible on the Material Select Page and it selects all materials for this LotType -->
					{#if whitelistModalUtils.showFourthActionButton(view) && $currentLotType !== LotType.TRANSPORT}
						<div class="fourth-action-button">
							<Button variant="secondary" color="grey" onclick={selectAllMaterials}
								>{fourthButtonText}</Button
							>
						</div>
					{/if}
				</div>
				<div>
					<!-- this is only visible on the startpage and selects all Materials for LotTypes and jumps to the Overview page -->
					{#if whitelistModalUtils.showSecondActionButton(view)}
						<div class="second-action-button">
							<Button variant="secondary" color="green" onclick={selectAllLotTypesAndMaterials}
								>{$t('UI.whitelist.modal.startpage.secondaryButton')}</Button
							>
						</div>
					{/if}
					<!-- the next button -->
					<div class="first-action-button">
						<Tooltip
							bind:this={overviewWeiterTooltip}
							placementTtip={TooltipPlacements.TOP}
							msg={overviewWarningMsg}
							maxWidthTtip={350}
							trigger="manual"
							isHtml
						>
							<Button
								variant="primary"
								color="green"
								disabled={firstActionButtonDisabled}
								onclick={handleFirstActionButton}
							>
								{whitelistModalUtils.getFirstActionButtonText(view)}
							</Button>
						</Tooltip>
					</div>
					{#if showQuickFinishButton}
						<div class="quick-finish-button">
							<Button
								variant="primary"
								color="green"
								disabled={firstActionButtonDisabled}
								onclick={handleQuickFinishToOverview}>{$t('UI.button.done')}</Button
							>
						</div>
					{/if}
				</div>
			</div>
		</div>
	</div>
{/if}

<style lang="scss">
	@import '$styles/modals'; // also includes global.scss

	.whitelist-modal-header {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		width: 100%;

		:global(.breadcrumbs) {
			flex: 1;
		}
	}

	p {
		font-size: 0.75rem;
		line-height: 20px;
		text-align: left;
	}

	h2 {
		text-align: left;
		width: max-content;
	}

	.actual-content {
		width: 100%;
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: self-start;
		row-gap: 0;
	}

	div.whitelist-modal-title {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: flex-start;
		column-gap: 8px;
		width: 100%;

		& li {
			cursor: pointer;

			&:hover {
				text-decoration: underline;
				color: var(--color-blue-500);
			}
		}
	}

	.modal-message {
		margin-top: 0.75rem;
		margin-bottom: 0;
	}

	.whitelist-button-actions {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: space-between;
		width: 100%;
		border-radius: 0 0 6px 6px;

		& > div {
			display: flex;
			flex-direction: row;
			align-items: center;
			justify-content: center;
			column-gap: 6px;
		}
	}

	.modal {
		justify-content: center;
		background: rgba(58, 58, 58, 0.4);
		pointer-events: all;
	}

	.wrapper-whitelist-progress-bar {
		width: inherit;
		overflow-x: hidden;
		display: grid;
		place-items: center center;
		margin-top: 24px;
		margin-bottom: var(--progress-bar-margin-bottom, 24px);
	}

	div.whitelist-view-header {
		padding-bottom: 0.7rem;

		& > span {
			font-family: Roboto, sans-serif;
			font-style: normal;
			font-weight: 500;
			font-size: 0.75rem;
			line-height: 16px;
			color: black;
		}
	}
</style>
